import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Employee, EmployeeSyncStatus, PaginatedResponse, ReasonForAbsenceLug } from '../models/employee.model';
import { NotificationService } from './notification.service';
import { extractHttpErrorMessage } from '../shared/http-error.util';

// Zwei bewusst unterschiedliche Formen in diesem Service - kein Versehen, siehe Audit-Historie:
//
// 1) loadEmployees()/refreshEmployeesInPlace() + getEmployees()/getTotalEmployees()/getLoading():
//    signal-basierter, geteilter Cache. EmployeeTableComponent, SickLeaveOverviewComponent und
//    SickLeaveFormComponent lesen denselben Signal-Stand, OHNE selbst neu zu laden - nur
//    EmployeeTableComponent ruft loadEmployees() auf. Eine Umstellung auf Observable würde diesen
//    geteilten Cache auflösen und jeden Konsumenten zwingen, selbst zu laden/zu subscriben.
// 2) updateEmployeeEmail()/retryEmailSync()/syncEmployees()/updateReasonForAbsence()/
//    getReasonCodes()/getSyncStatus(): klassische Einzel-Aktionen bzw. einmalige Abfragen, die der
//    (einzige) Aufrufer per .subscribe() mit eigener Erfolgs-/Fehlerbehandlung, forkJoin() oder
//    Polling-Anstoß kombiniert (siehe employee-table.component.ts). Observable ist hier die
//    natürliche Form für diese RxJS-Komposition.
//
// Die Notification-Seiteneffekte (tap/catchError → notificationService.show()) bei den
// Observable-Methoden bleiben bewusst hier und wurden NICHT an die Aufrufer verschoben: Dasselbe
// Muster (Service ruft NotificationService direkt) ist bereits durchgängige Konvention in
// SickLeaveService und wird dort weiterhin genutzt - eine Extraktion nur hier würde die Codebase
// inkonsistenter statt konsistenter machen, für einen reinen Verantwortlichkeits-Feinschliff ohne
// Verhaltensänderung. Siehe employee.service.spec.ts für die bestehende Testabdeckung dieses
// Verhaltens.
@Injectable({
    providedIn: 'root'
})
export class EmployeeService {
    private apiUrl = `${environment.apiUrl}/employees`;
    private employees = signal<Employee[]>([]);
    private totalEmployees = signal<number>(0);
    private loading = signal<boolean>(false);

    constructor(
        private http: HttpClient,
        private notificationService: NotificationService
    ) {}

    public loadEmployees(page: number = 1, limit: number = 25): void {
        this.loading.set(true);
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString());

        this.http.get<PaginatedResponse<Employee>>(this.apiUrl, { params }).subscribe({
            next: (response) => {
                this.employees.set(response.data ?? []);
                this.totalEmployees.set(response.total ?? 0);
                this.loading.set(false);
            },
            error: (error) => {
                this.employees.set([]);
                this.totalEmployees.set(0);
                this.loading.set(false);
                this.notificationService.show(this.handleError(error), 'error');
            }
        });
    }

    // Für Hintergrund-Polling (z.B. Email-Sync-Status nach einer Bearbeitung): lädt die aktuelle
    // Seite, ersetzt aber NICHT das employees-Array als Ganzes, sondern nur die Objektreferenzen
    // der Mitarbeiter, deren Daten sich tatsächlich geändert haben. So bleibt die Objektidentität
    // unveränderter Zeilen erhalten (wichtig für *ngFor ohne trackBy) und loading() wird nicht
    // angerührt, damit das Polling nicht bei jedem Tick die gesamte Tabelle neu rendert bzw. den
    // Scroll-Zustand der Liste stört.
    public refreshEmployeesInPlace(page: number = 1, limit: number = 25): void {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString());

        this.http.get<PaginatedResponse<Employee>>(this.apiUrl, { params }).subscribe({
            next: (response) => {
                const incoming = response.data ?? [];
                this.employees.update(current =>
                    current.map(existing => {
                        const updated = incoming.find(e => e.datevEmployeeNumber === existing.datevEmployeeNumber);
                        return updated && !this.sameEmployeeData(existing, updated) ? updated : existing;
                    })
                );
            },
            // Bewusst kein Fehler-Feedback hier: dieser Refresh läuft unauffällig im Hintergrund
            // (Polling-Tick), ein einzelner Fehlschlag soll den Nutzer nicht stören - der nächste
            // Tick versucht es erneut.
            error: () => {}
        });
    }

    private sameEmployeeData(a: Employee, b: Employee): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    private handleError(error: HttpErrorResponse): string {
        return extractHttpErrorMessage(error);
    }

    getEmployees() {
        return this.employees.asReadonly();
    }

    getTotalEmployees() {
        return this.totalEmployees.asReadonly();
    }

    getLoading() {
        return this.loading.asReadonly();
    }

    updateEmployeeEmail(employeeNumber: string, email: string): Observable<any> {
        return this.http.patch(`${this.apiUrl}/${employeeNumber}/email`, { email }).pipe(
            tap(() => {
                this.notificationService.show('E-Mail-Adresse wurde erfolgreich aktualisiert.', 'success');
            }),
            catchError((error: HttpErrorResponse) => {
                this.notificationService.show(this.handleError(error), 'error');
                return throwError(() => error);
            })
        );
    }

    retryEmailSync(employeeNumber: string | number): Observable<any> {
        return this.http.post(`${this.apiUrl}/${employeeNumber}/email/retry-sync`, {}).pipe(
            tap(() => {
                this.notificationService.show('E-Mail-Synchronisierung mit DATEV wird erneut versucht.', 'success');
            }),
            catchError((error: HttpErrorResponse) => {
                this.notificationService.show(this.handleError(error), 'error');
                return throwError(() => error);
            })
        );
    }

    syncEmployees(): Observable<any> {
        return this.http.post(`${this.apiUrl}/sync-employees`, {}).pipe(
            tap(() => {
                this.notificationService.show('Voller Mitarbeiter-Abgleich mit DATEV wird angestoßen.', 'success');
            }),
            catchError((error: HttpErrorResponse) => {
                this.notificationService.show(this.handleError(error), 'error');
                return throwError(() => error);
            })
        );
    }

    getSyncStatus(): Observable<EmployeeSyncStatus> {
        return this.http.get<EmployeeSyncStatus>(`${this.apiUrl}/sync-status`);
    }

    getReasonCodes(): Observable<ReasonForAbsenceLug[]> {
        return this.http.get<ReasonForAbsenceLug[]>(`${environment.apiUrl}/absences/reason-codes`);
    }

    updateReasonForAbsence(employeeNumber: string, reasonForAbsence: string): Observable<any> {
        return this.http.patch(`${this.apiUrl}/${employeeNumber}/reason-for-absence`, { reason_for_absence: reasonForAbsence }).pipe(
            tap(() => {
                this.notificationService.show('Abwesenheitsschlüssel wurde erfolgreich aktualisiert.', 'success');
            }),
            catchError((error: HttpErrorResponse) => {
                this.notificationService.show(this.handleError(error), 'error');
                return throwError(() => error);
            })
        );
    }
}
